import { memo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Box,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { green } from "@mui/material/colors";
import { nuclearn_api_url } from "src/App";
import { authorizedUser } from "src/helpers/Auth";
import { formatFileSize } from "src/helpers/formatFileSize";
import { useArtifactProvenance } from "src/views/assistant/services/ArtifactService";
import ProvenanceExportButton from "src/views/assistant/components/sources/ProvenanceExportButton";
import { useProjectsLayoutStore } from "src/views/assistant/projects/stores/projectsLayoutStore";
import { useChatContext } from "../../context/ChatContext";
import AddToProjectButton from "./AddToProjectButton";

interface FileDownloadProps {
  artifactId: number;
  filename: string;
  fileSize?: number;
  /** Assistant message id, for the message-scoped provenance export. */
  messageId?: string;
}

/** Where an in-session "Add to Project" landed the generated file. */
interface AddedLocation {
  folderId: number;
  folderName: string | null;
  fileName: string;
}

/**
 * Generated-document chat card (#20423 WS7): filename + download, the
 * provenance summary line when the artifact carries a source-provenance
 * manifest, Add to Project, and — once the file lands in a project — an
 * "Added to {project}" confirmation with a "View in Project Files" jump that
 * highlights the file row via the projects layout store.
 */
const FileDownload = memo(
  ({ artifactId, filename, fileSize, messageId }: FileDownloadProps) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloaded, setDownloaded] = useState(false);
    const [addedTo, setAddedTo] = useState<AddedLocation | null>(null);
    const history = useHistory();
    const { dialogueId } = useChatContext();

    // Sourced-claim count for the summary line; the generation payload itself
    // carries no provenance, so it rides GET /artifacts/{id}/provenance (a
    // manifest-less artifact returns claim_count 0 and the line is omitted).
    const { data: provenance } = useArtifactProvenance(artifactId);
    const claimCount = provenance?.claim_count ?? 0;

    const handleDownload = async () => {
      setIsDownloading(true);
      try {
        const response = await fetch(
          `${nuclearn_api_url}/artifacts/${artifactId}/download`,
          {
            method: "GET",
            headers: {
              ...(authorizedUser().Authorization
                ? { Authorization: authorizedUser().Authorization as string }
                : {}),
            },
          },
        );

        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          const downloadLink = document.createElement("a");
          downloadLink.href = blobUrl;
          downloadLink.download = filename;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(blobUrl);
          setDownloaded(true);
        } else {
          console.error("Download failed:", response.status);
        }
      } catch (error) {
        console.error("Download error:", error);
      } finally {
        setIsDownloading(false);
      }
    };

    // Jump to the file's row in the projects workspace: open+expand the tree
    // to its folder and set the citation focus so the row scrolls/highlights
    // (the same store mechanism project-file citations use, 6.6.6).
    const handleViewInProject = () => {
      if (!addedTo) return;
      const store = useProjectsLayoutStore.getState();
      store.setTreeOpen(true);
      store.expandFolder(addedTo.folderId);
      store.setCitationFileFocus(addedTo.folderId, addedTo.fileName);
      history.push(`/assistant/projects/folder/${addedTo.folderId}`);
    };

    return (
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1,
          bgcolor: "action.hover",
          my: 1,
        }}
        data-testid="aa-generated-doc-card"
      >
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          <Button
            variant="contained"
            size="small"
            startIcon={
              isDownloading ? (
                <CircularProgress size={16} color="inherit" />
              ) : downloaded ? (
                <CheckCircleIcon sx={{ color: green[500] }} />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={handleDownload}
            disabled={isDownloading}
            sx={{ minWidth: 120 }}
          >
            {isDownloading
              ? "Downloading..."
              : downloaded
                ? "Downloaded"
                : "Download"}
          </Button>
          <AddToProjectButton
            artifactId={artifactId}
            filename={filename}
            onAdded={setAddedTo}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight="medium" noWrap>
              {filename}
            </Typography>
            {fileSize && (
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(fileSize)}
              </Typography>
            )}
          </Box>
          {claimCount > 0 && dialogueId != null && messageId && (
            <Box sx={{ ml: "auto" }}>
              <ProvenanceExportButton
                dialogueId={dialogueId}
                messageId={messageId}
                compact
              />
            </Box>
          )}
        </Stack>

        {claimCount > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.75 }}
            data-testid="aa-generated-doc-provenance-line"
          >
            Generated by AtomAssist · {claimCount} sourced{" "}
            {claimCount === 1 ? "claim" : "claims"} with full provenance
          </Typography>
        )}

        {addedTo && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 0.75 }}
            data-testid="aa-generated-doc-added"
          >
            <CheckCircleIcon sx={{ fontSize: 16, color: green[600] }} />
            <Typography variant="caption" color="text.secondary">
              Added to {addedTo.folderName ?? "this project"}
            </Typography>
            <Button
              size="small"
              variant="text"
              startIcon={<FolderOpenIcon sx={{ fontSize: "1rem" }} />}
              onClick={handleViewInProject}
              data-attr="projects:btn:view-in-project-files"
              sx={{ textTransform: "none", fontSize: "0.75rem", py: 0 }}
            >
              View in Project Files
            </Button>
          </Stack>
        )}
      </Box>
    );
  },
);

FileDownload.displayName = "FileDownload";

export default FileDownload;
